// publicRoutability — ONE answer to one question:
//
//     Can this destination be linked publicly right now?
//
// WHY THIS EXISTS
// An internal-link audit on 2026-08-15 found 117 dead internal links. None was a
// random typo. They were six repeatable failures: draft listings, private
// listings, draft showcases, held destinations, retired categories, and stale
// geography kept inside stored editorial HTML. Every one told a crawler "there
// is something here", then answered 404.
//
// The rule is deliberately centralised. Scattering "is this ok to link?" checks
// through The Edit is what produced the mess: each surface made its own guess,
// none agreed, and none of them knew about the manifest. There is now exactly
// one predicate, and every link-emitting surface must ask it.
//
// THE KEY DESIGN CHOICE: THIS RUNS AT RENDER TIME, NOT AS A CONTENT MIGRATION.
// Rewriting the stored HTML would fix today's 117 and then rot, because
// eligibility changes and stored text does not. Evaluating on every render means
// promoting /italy/lake-como makes every reference to it across the whole site
// become a link automatically, with no migration and no article edits. Equally,
// putting a venue back to draft silently withdraws its links. The content says
// what it means; the routing layer decides whether that is a link today.
//
// FAIL CLOSED. If we cannot prove a destination is public, we do not link it. A
// missing link is a cosmetic loss; a link to a private prospect's page is a
// commercial and privacy problem, and a link to a 404 is a crawl-budget and
// trust problem. The asymmetry is not close.
import { REGIONAL_HUBS, CITY_HUBS, EDITORIAL_CATEGORY_HUBS, GLOBAL_CATEGORY_HUBS, STATIC_PAGES, UTILITY_PAGES } from '../../supabase/functions/_shared/searchManifest.js';
import { listingPaths, showcasePaths, editorialPaths, reviewPaths } from '../generated/publicPaths.js';

const strip = (p) => {
  if (typeof p !== 'string') return '';
  const noHash = p.split('#')[0].split('?')[0];
  return noHash.length > 1 ? noHash.replace(/\/+$/, '') : noHash;
};

const eligibleUrls = (list) => (list || []).filter(e => e && e.eligible !== false).map(e => strip(e.url));
const heldUrls     = (list) => (list || []).filter(e => e && e.eligible === false).map(e => strip(e.url));

// Hubs and categories the manifest says are live. Held entries are excluded
// here AND listed explicitly below, because "absent from the manifest" and
// "present but held" must both block, and only one of them is enumerable.
const ELIGIBLE = new Set([
  ...eligibleUrls(REGIONAL_HUBS),
  ...eligibleUrls(CITY_HUBS),
  ...eligibleUrls(EDITORIAL_CATEGORY_HUBS),
  ...eligibleUrls(GLOBAL_CATEGORY_HUBS),
  ...eligibleUrls(STATIC_PAGES),
  ...eligibleUrls(UTILITY_PAGES),
]);
const HELD = new Set([
  ...heldUrls(REGIONAL_HUBS),
  ...heldUrls(CITY_HUBS),
  ...heldUrls(EDITORIAL_CATEGORY_HUBS),
  ...heldUrls(GLOBAL_CATEGORY_HUBS),
]);

const PUBLIC_LISTINGS  = new Set((listingPaths  || []).map(strip));
const PUBLIC_SHOWCASES = new Set((showcasePaths || []).map(strip));
const PUBLIC_ARTICLES  = new Set((editorialPaths || []).map(strip));
// A /reviews child exists only where the listing has an approved review.
const PUBLIC_REVIEWS   = new Set((reviewPaths || []).map(strip));

// Countries are shallow, always-served pages (/italy, /england). Region and city
// hubs beneath them are the curated, editorially-gated tier and must never be
// inferred from a country being live.
//
// Derived from where public LISTINGS actually live, not from REGIONAL_HUBS. The
// first attempt used eligible two-segment hub URLs and silently omitted the USA:
// it has three published venues and no promoted region hub, so every /usa/... URL
// failed closed and the Beverly Hills city hub was refused a link. Inventory is
// the honest source for "is this country served"; hub promotion is a separate,
// editorial question and answering it here conflated the two.
const COUNTRY_SEGMENTS = new Set([
  ...[...PUBLIC_LISTINGS].map(u => u.split('/').filter(Boolean)[0]),
  ...[...ELIGIBLE].filter(u => u.split('/').filter(Boolean).length === 2)
    .map(u => u.split('/').filter(Boolean)[0]),
].filter(Boolean));

const CATEGORY_LIKE = /^(wedding-venues|wedding-planners|photographers|videographers|bridal-boutiques|bridal-dresses|bridal-wear|guest-attire|accessories|jewellers|hair-makeup|health-beauty|florists|styling-decor|event-design|event-production|caterers|wedding-cakes|entertainment|celebrants|transport|stationery|cakes|music)$/;

/**
 * The predicate. Returns true only when `href` is a destination the public site
 * will actually serve. External links, anchors and query-only links are not our
 * business and pass through untouched.
 */
export function isPubliclyRoutable(href) {
  if (typeof href !== 'string' || !href) return false;

  // Not an internal path: someone else's problem, and not a 404 we can cause.
  if (/^(https?:|mailto:|tel:|sms:|data:)/i.test(href)) return true;
  if (href.startsWith('#') || href.startsWith('?')) return true;
  if (!href.startsWith('/')) return true;

  const p = strip(href);
  if (p === '/' || p === '') return true;

  // A held hub is held EVERYWHERE. Checked before the allowlist so a stale
  // duplicate entry can never accidentally promote something.
  if (HELD.has(p)) return false;
  if (ELIGIBLE.has(p)) return true;

  if (PUBLIC_LISTINGS.has(p)) return true;
  if (PUBLIC_SHOWCASES.has(p)) return true;
  if (PUBLIC_REVIEWS.has(p)) return true;

  const seg = p.split('/').filter(Boolean);

  // A showcase is public only if its OWN row is published. Never inferred from
  // the listing: Six Senses Southern Dunes has a public listing and a draft
  // showcase, and /showcase/six-senses-southern-dunes 404s.
  if (seg[0] === 'showcase') return false;

  // Editorial articles: /lwd-edit/<slug>. Category hubs under /lwd-edit/category
  // are governed by the manifest and already resolved above, so anything still
  // unresolved there is retired or never existed.
  // An editorial article is linkable only if it is PUBLISHED. Returning true for any
  // two-segment /lwd-edit/<slug> was too permissive, and Step 1 turned that latent bug
  // into live 404s: the category hubs now emit real anchors, so an unpublished article
  // stopped being a dead onClick and became a dead link.
  if (seg[0] === 'lwd-edit') return seg.length === 2 && PUBLIC_ARTICLES.has(p);

  // Geo shapes. A bare /country is fine. Anything deeper is a curated hub, and
  // if the manifest did not list it above, it is held or does not exist.
  if (seg.length === 1) return COUNTRY_SEGMENTS.has(seg[0]) || ELIGIBLE.has(p);
  if (COUNTRY_SEGMENTS.has(seg[0])) {
    // /country/category is a real generated tier (tierB in the sitemap).
    if (seg.length === 2) return CATEGORY_LIKE.test(seg[1]);
    // /country/region/category and /country/region/city/category are generated
    // from published listings, so they exist wherever public inventory does.
    if (seg.length === 3) return CATEGORY_LIKE.test(seg[2]);
    if (seg.length === 4) return CATEGORY_LIKE.test(seg[3]);
    // /country/region/category/slug is a listing, and a listing is linkable only
    // if PUBLIC_LISTINGS said so above. Reaching here means draft, private, or a
    // stale path shape (the lazio / campania / united-kingdom family).
    return false;
  }

  // Unknown shape. Fail closed.
  return false;
}

/**
 * Rewrites a sanitized HTML string so anchors pointing at non-routable
 * destinations become plain text, keeping the words and dropping the link.
 * Browser-only (needs DOMParser); returns the input untouched elsewhere so
 * server-side callers degrade to current behaviour rather than throwing.
 */
export function gateAnchors(html) {
  if (!html || typeof html !== 'string') return html;
  if (typeof DOMParser === 'undefined') return html;
  if (!html.includes('<a')) return html;
  try {
    const doc = new DOMParser().parseFromString(`<div id="__root">${html}</div>`, 'text/html');
    const root = doc.getElementById('__root');
    let changed = false;
    root.querySelectorAll('a[href]').forEach((a) => {
      if (isPubliclyRoutable(a.getAttribute('href'))) return;
      changed = true;
      const span = doc.createElement('span');
      span.setAttribute('data-unlinked', 'not-public');
      while (a.firstChild) span.appendChild(a.firstChild);
      a.replaceWith(span);
    });
    return changed ? root.innerHTML : html;
  } catch {
    return html; // never let the gate break a page
  }
}
